import React, { Fragment } from 'react';
import dateformat from 'dateformat';
import ViewForm, { ViewFormField } from './ViewForm';
import { GridColumn } from '@atlaskit/page';
import { TextField, DatePickerField, SelectField, FormSection } from '../../../components/FormFields';
import FieldError from '../../../components/FieldError';
import Button from '@atlaskit/button';
import ArrowDownIcon from '@atlaskit/icon/glyph/arrow-down';
import { FormFooter } from '@atlaskit/form';
import { EditViewContext } from './EditViewContext';
import i18n from 'i18next';
import { Trans } from 'react-i18next';
import styled from 'styled-components';

export const FirmwareFields = ({
  name,
  status,
  version,
  applicability,
  fileName,
  file,
  onFileChange,
  onChange,
  fetching,
  dictionaries,
  getDictionaries,
  onDownload,
  isEditMode,
  errors,
  permissions
}) => (
  <Fragment>
    <GridColumn medium={12}>
      <TextField
        name='name'
        label={i18n.t('fota:Name')}
        placeholder={i18n.t('fota:Enter firmware name')}
        required={true}
        onChange={onChange}
        isDisabled={fetching}
        value={name}
      />
    </GridColumn>
    <GridColumn medium={6}>

      <FieldError error={errors.status} />
      <SelectField
        name='status'
        label={i18n.t('fota:Status')}
        placeholder={i18n.t('fota:Select status')}
        isMulty={false}
        value={status}
        required={true}
        onChange={onChange}
        data={dictionaries.firmwareStatuses.data}
        fetching={dictionaries.firmwareStatuses.fetching}
        getData={getDictionaries('firmwareStatuses')}
        isDisabled={fetching}
      />
    </GridColumn>
    <GridColumn medium={6}>
      <FieldError error={errors.applicability} />
      <SelectField
        name='applicability'
        label={i18n.t('fota:Applicability')}
        placeholder={i18n.t('fota:Select applicability')}
        isMulty={false}
        value={applicability}
        required={true}
        onChange={onChange}
        data={dictionaries.applicabilities.data}
        fetching={dictionaries.applicabilities.fetching}
        getData={getDictionaries('applicabilities')}
        isDisabled={fetching}
      />
    </GridColumn>
    <GridColumn medium={6}>
      <TextField
        name='version'
        label={i18n.t('fota:Version')}
        placeholder={i18n.t('fota:Enter Version')}
        required={true}
        onChange={onChange}
        isDisabled={fetching}
        value={version}
      />
    </GridColumn>
    <GridColumn medium={6}>

      <FormSection label={'ok'}>
        <FileInputContainer>

          <ButtonGroup required={true}>
            <FieldError error={errors.file} />
            <FileInputButton htmlFor="file-upload" required={true} disabled={fetching && permissions?.upload}>
              {file?.name ?? i18n.t('fota:Upload File')}
            </FileInputButton>
            {(permissions?.upload ?? fileName) && (<DownloadButton
              disabled={fileName}
              onClick={onDownload}
              title={`${i18n.t('fota:Download File')} ${fileName}`}>
              <ArrowDownIcon size="small" />
              {fileName ? `${i18n.t('fota:Download File')} ${fileName}` : i18n.t('fota:Not Available File')}
            </DownloadButton>
            )}
          </ButtonGroup>
          <HiddenFileInput
            id="file-upload"
            onChange={onFileChange}
            disabled={fetching}
            accept='.bin'
          />
        </FileInputContainer>
      </FormSection>
    </GridColumn>
  </Fragment>
);

const FirmwareEditForm = ({
  name,
  status,
  version,
  applicability,
  file,
  fileName,
  onChange,
  onFileChange,
  dictionaries,
  getDictionaries,
  fetching,
  onCancel,
  onDownload,
  errors
}) => (
  <Fragment>
    <FirmwareFields
      name={name}
      status={status}
      version={version}
      file={file}
      fileName={fileName}
      applicability={applicability}
      onChange={onChange}
      onFileChange={onFileChange}
      dictionaries={dictionaries}
      getDictionaries={getDictionaries}
      fetching={fetching}
      onDownload={onDownload}
      errors={errors}
    />
    <GridColumn medium={14}>
      <FormFooter>
        <ButtonGroup>
          <Button appearance='subtle' isDisabled={fetching} onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type='submit' isDisabled={fetching} isLoading={fetching} appearance='danger'>
            <Trans>Save</Trans>
          </Button>
        </ButtonGroup>
      </FormFooter>
    </GridColumn>
  </Fragment>
);

const FirmwareViewForm = ({
  name,
  status,
  version,
  applicability,
  file,
  fileName
}) => (
  <Fragment>
    <ViewFormField label={i18n.t('fota:Name')} value={name} gridColumn={4} />
    <ViewFormField label={i18n.t('fota:Status')} value={status?.name} gridColumn={3} />
    <ViewFormField label={i18n.t('fota:Version')} value={version} gridColumn={3} />
    <ViewFormField label={i18n.t('fota:Applicability')} value={applicability?.name} gridColumn={4} />
    <ViewFormField label={i18n.t('fota:File')} value={fileName} gridColumn={3} />
  </Fragment>
);

export default props => {
  const formName = 'firmware';
  return (
    <EditViewContext.Consumer>
      {({ edit, permissions, onChangeEditMode, onChange }) =>
        /*permissions && permissions.info  &&*/(
        <ViewForm
          formName={formName}
          title={i18n.t('fota:Firmware')}
          actionName={ /*permissions.info.edit && */ i18n.t('Edit')}
          action={onChangeEditMode(formName)}
          editMode={edit === formName}
        >
          {edit === formName
            ? <FirmwareEditForm {...props} onChange={onChange} />
            : <FirmwareViewForm {...props} />
          }
        </ViewForm>
      )
      }
    </EditViewContext.Consumer>
  );
};

// Стилизуем контейнер для загрузки файлов
const FileInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`;

// Стилизуем скрытый input для загрузки файла
const HiddenFileInput = styled.input.attrs({ type: 'file' })`
  display: none;
`;
const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-start; 
  align-items: center; 
  gap: 8px; 
`;

const DownloadButton = styled.label`
  background-color: #0052CC;
  max-width: 200px;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  line-height: 20px;
  border: none;
  display: block; /* Убираем flex для выравнивания текста */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: #003D99;
  }
`;

const FileInputButton = styled.label`
    max-width: 200px;
   word-wrap: break-word;
  background-color: #0052CC;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  &:hover {
    background-color: #003D99;
  }
`;